import Immutable from 'immutable';

import {
	webapi,
	createModuleState,
	REPORTED_EXCEPTION,
	showMessageBox,
	setRootModuleState,
} from 'ps-gui';
import { initializeLookups } from '../../data/lookup';
import { getPhrase } from '../../data/phrases';
import { initializeSession } from '../../data/session';
import MainPageModel from '../mainpagemodel';

const EMPTY_MAP = Immutable.Map({});
const EMPTY_STATE = createModuleState('Login');

const NOSMS = false;
export default class LoginModel {
	#data;

	constructor(data) {
		this.#data = data || EMPTY_MAP;
	}

	#setProperty(name, value) {
		return new LoginModel(this.#data.set(name, value));
	}

	showForgottenPasswordForm() {
		return this.#setProperty('forgottenPasswordForm', true);
	}

	hideForgottenPasswordForm() {
		return this.#setProperty('forgottenPasswordForm', false);
	}

	setBankIdMatcherInfo(bankIdentityId, matchAuthToken, errorCode) {
		if (!bankIdentityId) {
			return this.#setProperty('bankIdMatcherInfo', null);
		}
		return this.#setProperty(
			'bankIdMatcherInfo',
			EMPTY_MAP.set('id', bankIdentityId).set('authenticationCode', matchAuthToken).set('errorCode', errorCode)
		);
	}

	hasBankIdMatcherInfo() {
		return !!this.#data.get('bankIdMatcherInfo');
	}

	getBankIdMatcherInfo() {
		return this.#data.get('bankIdMatcherInfo');
	}

	async createPasswordChangeAuthenticationCode(languageId) {
		const data = this.#data;
		let codeRes;
		try {
			codeRes = await webapi.execute('/api/user/createPasswordChangeAuthenticationCode', {
				userName: data.get('userName'),
				languageId
			});
			if (codeRes === REPORTED_EXCEPTION) {
				return this;
			}
		} catch (ex) {
			if (ex === 'userInvalidLogin') {
				showMessageBox(getPhrase('invalidUserName'));
				return this.#setProperty('userNameError', getPhrase('invalidUserName'));
			} else if (ex === 'userExpired') {
				showMessageBox(getPhrase('userExpired'));
				return this.#setProperty('userNameError', getPhrase('userExpired'));
			} else {
				showMessageBox(ex);
				return this;
			}
		}
		let model = this.#setProperty('userNameError', null);
		return model.#setProperty('passwordChangeAuthCode', Immutable.fromJS(codeRes));
	}

	async otpLogin() {
		const data = this.#data;
		let res;
		try {
			res = await webapi.execute('/api/user/otpLogin', {
				authenticationCodeId: data.getIn(['loginAuthCode', 'codeId']),
				authenticationCode: data.get('smsCode')
			});
			if (res === REPORTED_EXCEPTION) {
				return this;
			}
		} catch (ex) {
			if (ex === 'invalidAuthenticationCode') {
				await showMessageBox(getPhrase('invalidSmsCode'));
				return new LoginModel();
			} else if (ex === 'authenticationCodeExpired') {
				await showMessageBox(getPhrase('smsCodeExpired'));
				return new LoginModel();
			}
		}
		await initializeSession(res.token, {
			overrideUserLanguage: true
		});
		await initializeLookups();
		const bmi = this.#data.get('bankIdMatcherInfo');
		if (bmi && bmi.get('id') != -1) {
			await webapi.execute('/api/bi/setBankIdentityUser', {
				id: bmi.get('id'),
				authenticationCode: bmi.get('authenticationCode')
			});
		}
		let rootModel = new MainPageModel();
		setRootModuleState(rootModel.getState());
		return null;
	}

	async login(languageId) {
		const data = this.#data;

		const userName = (data.get('userName') || '').trim();
		const password = (data.get('password') || '').trim();
		if (!userName || !password) {
			return this.#setProperty('loginError', getPhrase('invalidCredentials'));
		}
		let codeRes;
		if (NOSMS) {
			codeRes = {
				phone: '+420608663713',
				codeId: 21
			};
		} else {
			try {
				codeRes = await webapi.execute('/api/user/createLoginAuthenticationCode', {
					userName,
					password,
					languageId
				});
				if (codeRes === REPORTED_EXCEPTION) {
					return this;
				}
			} catch (ex) {
				if (ex === 'userInvalidLogin') {
					return this.#setProperty('loginError', getPhrase('invalidCredentials'));
				} else if (ex === 'userExpired') {
					return this.#setProperty('loginError', getPhrase('userExpired'));
				} else if (ex === 'errorSendingSms') {
					showMessageBox(getPhrase('errorSendingLoginCodeSms'));
					return this;
				} else {
					showMessageBox('chyba - ' + ex);
					return this;
				}
			}
		}
		let model = this.#setProperty('loginError', null);
		return model.#setProperty('loginAuthCode', Immutable.fromJS(codeRes));
	}

	getData() {
		return this.#data;
	}

	getState() {
		return EMPTY_STATE.set('data', this.#data);
	}

	getPhone() {
		return this.#data.getIn(['loginAuthCode', 'phone']);
	}

	getPasswordChangeAuthCode() {
		return this.#data.get('passwordChangeAuthCode');
	}

	// Components switch functions

	isLoginAuthCode() {
		return this.#data.get('loginAuthCode');
	}

	isPasswordChangeAuthCode() {
		return this.#data.get('passwordChangeAuthCode');
	}

	isForgottenPasswordForm() {
		return this.#data.get('forgottenPasswordForm');
	}
}

